import {
  type DefaultError,
  keepPreviousData,
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
  type UndefinedInitialQueryOptions,
  type UseQueryReturnType
} from '@tanstack/vue-query';
import axiosInstance from '@/plugins/axios';
import useAuthenticatedQuery from '@/composables/apiServices/useAuthenticatedQuery';
import useUserService from '@/composables/apiServices/useUserService';
import { EContactOrdering } from '@/types/services/user.service.enums';
import type {
  IAddressbookDeleteRequest,
  IAddressbookListResponse,
  IAddressbookRequestData,
  IAddressbookResponse,
  IAddressbookUpdateRequest,
  IContact,
  IContactDeleteRequest,
  IContactImportRequest,
  IContactImportResponse,
  IContactListData,
  IContactListResponse,
  IContactRequestData,
  IContactsDeleteRequest,
  IGroupAvatarResponse,
  IGroupDeleteRequest,
  IGroupRequestData,
  IGroupResponse,
  IRecentPartiesRequest,
  IRecentPartiesResponse
} from '@/types/services/contact.service';
import { type MaybeRef, type Ref, toValue } from 'vue';
import { computed } from 'vue';
import { type AxiosError, type AxiosResponse } from 'axios';
import { useI18n } from 'vue-i18n';
import useSnackbar from '../useSnackbar';
import { appendFormData } from '@/helpers/formData';

const useContactsService = () => {
  const { t } = useI18n();
  const { addSnackbar } = useSnackbar();
  const { userIndex, queriesEnabled } = useAuthenticatedQuery();
  const queryClient = useQueryClient();
  const { attributes } = useUserService();
  const { data: attributesData } = attributes();
  const contactOrdering = computed(
    () => attributesData.value?.contactOrdering ?? EContactOrdering.NAME
  );

  const getContact = (
    contactId: Ref<number>
  ): UseQueryReturnType<IContactListResponse, DefaultError> => {
    return useQuery({
      queryKey: ['authenticatedQuery', userIndex, 'contacts', contactId],
      queryFn: async ({ signal }) => {
        return (
          await axiosInstance.get<IContactListResponse>(
            `/u/${userIndex.value}/contacts/${contactId.value}`,
            { signal }
          )
        ).data;
      },
      enabled: queriesEnabled
    });
  };

  const recursiveQueryAllContacts = async (
    signal: AbortSignal,
    ordering: EContactOrdering,
    pageParam?: string
  ): Promise<IContactListData> => {
    const listResponse = (
      await axiosInstance.get<IContactListResponse>(`/u/${userIndex.value}/contacts`, {
        params: { cursor: pageParam },
        signal
      })
    ).data;
    if (listResponse.data.length > 0) {
      // If we have data, we need to check if there is a next page
      if (listResponse.nextCursor) {
        // If there is a next page, we need to recursively call this function
        const nextPageData = await recursiveQueryAllContacts(
          signal,
          ordering,
          listResponse.nextCursor
        );
        const allData = [...listResponse.data, ...nextPageData];
        if (!pageParam) {
          if (ordering === EContactOrdering.NAME) {
            return allData.sort((a, b) => a.fullName.localeCompare(b.fullName));
          }
          return allData;
        }
        return allData;
      }
      // If there is no next page, we can return the data
      if (!pageParam && ordering === EContactOrdering.NAME) {
        return listResponse.data.sort((a, b) => a.fullName.localeCompare(b.fullName));
      }
      return listResponse.data;
    }

    return []; // Return an empty array if no data is found
  };
  const getContactsList = () => {
    return useQuery<IContactListData, DefaultError>({
      queryKey: ['authenticatedQuery', userIndex, 'contacts', 'list', contactOrdering],
      queryFn: async ({ signal }) => {
        return recursiveQueryAllContacts(signal, contactOrdering.value);
      },
      enabled: queriesEnabled,
      placeholderData: keepPreviousData // This prevents tanstack from returning null when the query is loading
    });
  };

  // const getInfiniteContactsList = () => {
  //   return useInfiniteQuery<
  //     IContactListResponse,
  //     DefaultError,
  //     InfiniteData<IContactListResponse>,
  //     QueryKey,
  //     string | undefined
  //   >({
  //     queryKey: ['authenticatedQuery', userIndex, 'contacts', 'list'],
  //     queryFn: async ({ signal, pageParam }) => {
  //       // Possibly maybe move the filtering to here, but for now lets keep services strict and
  //       // only return what we actually get.
  //       // res.data = res.data.filter((c) => c.emails !== null)
  //       return (
  //         await axiosInstance.get(`/u/${userIndex.value}/contacts`, {
  //           params: { cursor: pageParam },
  //           signal
  //         })
  //       ).data;
  //     },
  //     getNextPageParam: (lastPage) => {
  //       if (lastPage && lastPage.nextCursor) {
  //         return lastPage.nextCursor;
  //       }
  //       return undefined;
  //     },
  //     getPreviousPageParam: (firstPage) => {
  //       if (firstPage && firstPage.previousCursor) {
  //         return firstPage.previousCursor;
  //       }
  //       return undefined;
  //     },
  //     initialPageParam: undefined,
  //     enabled: queriesEnabled,
  //     placeholderData: keepPreviousData // This prevents tanstack from returning null when the query is loading
  //   });
  // };

  const getRecentPartiesQueryOptions = (
    query: MaybeRef<string>
  ): UndefinedInitialQueryOptions<IRecentPartiesResponse | null> => {
    return queryOptions<IRecentPartiesResponse | null>({
      queryKey: ['authenticatedQuery', userIndex, 'contacts', 'recent', query],
      queryFn: async () => {
        const queryTrim = toValue(query).trim();
        if (!queryTrim || queryTrim.length > 255) {
          return null;
        }
        // Possibly maybe move the filtering to here, but for now lets keep services strict and
        // only return what we actually get.
        // res.data = res.data.filter((c) => c.emails !== null)
        return (
          await axiosInstance.get<IRecentPartiesResponse>(
            `/u/${userIndex.value}/mailbox/compose/recentParties?query=${queryTrim}`
          )
        ).data;
      },
      enabled: () => queriesEnabled.value,
      placeholderData: keepPreviousData // This prevents tanstack from returning null when the query is loading
    });
  };

  const getRecentParties = (
    query: Ref<string>
  ): UseQueryReturnType<IRecentPartiesResponse | null, DefaultError> => {
    return useQuery(getRecentPartiesQueryOptions(query));
  };

  const deleteRecentParty = () => {
    return useMutation<void, DefaultError, IContactDeleteRequest>({
      mutationKey: ['authenticatedQuery', userIndex, 'contact', 'delete'],
      mutationFn: async (body) => {
        await axiosInstance.put(`/u/${userIndex.value}/mailbox/compose/recentParties/${body.id}`, {
          disabled: true
        });
      },
      onSuccess() {
        addSnackbar({ message: t('settings.contacts.myContacts.delete.success'), type: 'success' });
        // Invalidate all recent parties queries since a single party can appear in multiple queries
        queryClient.invalidateQueries({
          queryKey: ['authenticatedQuery', userIndex, 'contacts', 'recent']
        });
      }
    });
  };

  const renameRecentParty = () => {
    return useMutation<void, DefaultError, IRecentPartiesRequest>({
      mutationKey: ['authenticatedQuery', userIndex, 'contact', 'delete'],
      mutationFn: async (body) => {
        await axiosInstance.put(`/u/${userIndex.value}/mailbox/compose/recentParties/${body.id}`, {
          name: body.name
        });
      },
      onSuccess() {
        addSnackbar({ message: t('settings.contacts.myContacts.delete.success'), type: 'success' });
        // Invalidate all recent parties queries since a single party can appear in multiple queries
        queryClient.invalidateQueries({
          queryKey: ['authenticatedQuery', userIndex, 'contacts', 'recent']
        });
      }
    });
  };

  const addContact = () => {
    return useMutation<IContact, AxiosError, IContactRequestData>({
      mutationKey: ['authenticatedQuery', userIndex, 'contacts', 'add'],
      mutationFn: async (contactData) => {
        const formData = new FormData();
        appendFormData(formData, contactData);
        if (contactData.avatarFile) {
          formData.append('avatar', contactData.avatarFile);
        }
        return (await axiosInstance.post<IContact>(`/u/${userIndex.value}/contacts`, formData))
          .data;
      },
      onSuccess: () => {
        addSnackbar({ message: t('settings.contacts.myContacts.added'), type: 'success' });
      }
    });
  };

  const updateContact = () =>
    useMutation<void, AxiosError, { id: string; data: IContactRequestData }>({
      mutationKey: ['authenticatedQuery', userIndex, 'contact', 'update'],
      mutationFn: async ({ id, data }) => {
        const formData = new FormData();
        appendFormData(formData, data);
        if (data.avatarFile) {
          formData.delete('avatar');
          formData.append('avatar', data.avatarFile);
        }
        await axiosInstance.post(`/u/${userIndex.value}/contacts/${id}`, formData, {
          params: { _method: 'PATCH' }
        });
      },
      onSuccess: () => {
        addSnackbar({ message: t('settings.contacts.myContacts.updated'), type: 'success' });
      }
    });

  const deleteContacts = () => {
    return useMutation<void, DefaultError, IContactsDeleteRequest>({
      mutationKey: ['authenticatedQuery', userIndex, 'contact', 'delete', 'multiple'],
      mutationFn: async (body) => {
        await axiosInstance.delete(`/u/${userIndex.value}/contacts`, { data: body });
      },
      onSuccess() {
        addSnackbar({ message: t('settings.contacts.myContacts.delete.success'), type: 'success' });
      }
    });
  };

  const getAddressbookList = (): UseQueryReturnType<IAddressbookListResponse, DefaultError> => {
    return useQuery({
      queryKey: ['authenticatedQuery', userIndex, 'addressbooks', 'list'],
      queryFn: async ({ signal }) => {
        return (
          await axiosInstance.get<IAddressbookListResponse>(
            `/u/${userIndex.value}/contacts/addressbooks`,
            { signal }
          )
        ).data;
      },
      enabled: queriesEnabled,
      placeholderData: keepPreviousData // This prevents tanstack from returning null when the query is loading
    });
  };

  const addAddressbook = () => {
    return useMutation<IAddressbookResponse, DefaultError, IAddressbookRequestData>({
      mutationKey: ['authenticatedQuery', userIndex, 'addressbooks', 'add'],
      mutationFn: async (body) => {
        return (
          await axiosInstance.post<IAddressbookResponse>(
            `/u/${userIndex.value}/contacts/addressbooks`,
            body
          )
        ).data;
      },
      onSuccess() {
        addSnackbar({ message: t('settings.contacts.addressbooks.added'), type: 'success' });
      }
    });
  };

  const deleteAddressbook = () => {
    return useMutation<void, DefaultError, IAddressbookDeleteRequest>({
      mutationKey: ['authenticatedQuery', userIndex, 'addressbooks', 'delete'],
      mutationFn: async (body) => {
        await axiosInstance.delete(`/u/${userIndex.value}/contacts/addressbooks/${body.id}`);
      },
      onSuccess() {
        addSnackbar({
          message: t('settings.contacts.addressbooks.delete.success'),
          type: 'success'
        });
      }
    });
  };

  const updateAddressbook = () =>
    useMutation<void, DefaultError, Partial<IAddressbookUpdateRequest>>({
      mutationKey: ['authenticatedQuery', userIndex, 'addressbooks', 'update'],
      mutationFn: async ({ id, name }) => {
        await axiosInstance.patch(`/u/${userIndex.value}/contacts/addressbooks/${id}`, { name });
      },
      onSuccess() {
        addSnackbar({ message: t('settings.contacts.addressbooks.updated'), type: 'success' });
      }
    });

  const getGroupsList = (): UseQueryReturnType<IGroupResponse, DefaultError> => {
    return useQuery({
      queryKey: ['authenticatedQuery', userIndex, 'groups', 'list'],
      queryFn: async ({ signal }) => {
        return (await axiosInstance.get(`/u/${userIndex.value}/contacts/groups`, { signal })).data;
      },
      enabled: queriesEnabled,
      placeholderData: keepPreviousData // This prevents tanstack from returning null when the query is loading
    });
  };

  const mapGroupDataToFormData = (groupData: IGroupRequestData): FormData => {
    const formData = new FormData();
    formData.append('name', groupData.name);

    // Append contacts
    groupData.contacts.forEach((contact: number, index: number) => {
      formData.append(`contacts[${index}]`, contact.toString());
    });

    // Append avatar file if it exists
    if (groupData.avatar) {
      formData.append('avatar', groupData.avatar);
    }
    return formData;
  };

  const addGroup = () => {
    return useMutation<IGroupResponse, AxiosError, IGroupRequestData>({
      mutationKey: ['authenticatedQuery', userIndex, 'groups', 'add'],
      mutationFn: async (body) => {
        const formData = mapGroupDataToFormData(body);
        return (
          await axiosInstance.post<IGroupResponse>(
            `/u/${userIndex.value}/contacts/groups`,
            formData
          )
        ).data;
      },
      onSuccess() {
        addSnackbar({ message: t('settings.contacts.groups.added'), type: 'success' });
      }
    });
  };

  const updateGroup = () =>
    useMutation<void, AxiosError, { id: number; data: IGroupRequestData }>({
      mutationKey: ['authenticatedQuery', userIndex, 'groups', 'update'],
      mutationFn: async ({ id, data }) => {
        const formData = mapGroupDataToFormData(data);
        await axiosInstance.post(`/u/${userIndex.value}/contacts/groups/${id}`, formData, {
          params: { _method: 'PATCH' }
        });
      },
      onSuccess() {
        addSnackbar({ message: t('settings.contacts.groups.updated'), type: 'success' });
      }
    });

  const getContactAvatar = (
    contactId: Ref<number | null>,
    hasAvatar?: Ref<boolean>
  ): UseQueryReturnType<string | null, DefaultError> => {
    return useQuery({
      queryKey: ['authenticatedQuery', userIndex, 'contacts', contactId, 'avatar'],
      queryFn: async ({ signal }) => {
        if (!contactId.value) {
          return null;
        }
        return (
          await axiosInstance
            .get(`/u/${userIndex.value}/contacts/${contactId.value}/avatar`, { signal })
            .catch(() => ({ data: null }))
        ).data;
      },
      enabled: computed(
        () => queriesEnabled.value && hasAvatar && hasAvatar.value && !!contactId.value
      )
    });
  };

  const getGroupAvatar = (
    groupId: Ref<number | null>,
    hasAvatar?: Ref<boolean>
  ): UseQueryReturnType<IGroupAvatarResponse | null, DefaultError> => {
    return useQuery({
      queryKey: ['authenticatedQuery', userIndex, 'contacts', 'group', groupId, 'avatar'],
      queryFn: async ({ signal }) => {
        if (!groupId.value) {
          return null;
        }
        return (
          await axiosInstance
            .get(`/u/${userIndex.value}/contacts/groups/${groupId.value}/avatar`, { signal })
            .catch(() => ({ data: null }))
        ).data;
      },
      enabled: computed(
        () => !!groupId.value && hasAvatar && hasAvatar.value && queriesEnabled.value
      )
    });
  };

  const deleteGroup = () => {
    return useMutation<void, DefaultError, IGroupDeleteRequest>({
      mutationKey: ['authenticatedQuery', userIndex, 'groups', 'delete'],
      mutationFn: async (body) => {
        await axiosInstance.delete(`/u/${userIndex.value}/contacts/groups/${body.id}`);
      },
      onSuccess() {
        addSnackbar({ message: t('settings.contacts.groups.delete.success'), type: 'success' });
      }
    });
  };

  const exportCSV = () => {
    return useMutation<Blob, DefaultError, string>({
      mutationKey: ['authenticatedQuery', userIndex, 'contacts', 'export', 'csv'],
      mutationFn: async (type) => {
        return (
          await axiosInstance.get(`/u/${userIndex.value}/contacts/export/${type}`, {
            responseType: 'blob',
            timeout: 0
          })
        ).data;
      }
    });
  };

  const importContacts = () => {
    return useMutation<
      AxiosResponse<null | IContactImportResponse>,
      AxiosError,
      IContactImportRequest
    >({
      mutationKey: ['authenticatedQuery', userIndex, 'contacts', 'import', 'csv'],
      mutationFn: async (importData) => {
        // Create FormData and append each field
        const formData = new FormData();
        formData.append('addressBookId', importData.addressBookId.toString());
        formData.append('type', importData.type);
        formData.append('contactImport', importData.contactImport);

        return await axiosInstance.post(`/u/${userIndex.value}/contacts/import`, formData, {
          timeout: 0
        });
      },
      onSuccess: (data) => {
        if (data.status === 204) {
          addSnackbar({ message: t('contacts.import.completed'), type: 'success' });
          return;
        }
        addSnackbar({
          message: data.data?.message ?? t('contacts.import.completed'),
          type: 'success'
        });
      }
    });
  };

  return {
    addContact,
    getContact,
    getContactsList,
    updateContact,
    deleteContacts,
    getRecentParties,
    getRecentPartiesQueryOptions,
    deleteRecentParty,
    renameRecentParty,
    getAddressbookList,
    addAddressbook,
    deleteAddressbook,
    updateAddressbook,
    getGroupsList,
    addGroup,
    getContactAvatar,
    updateGroup,
    getGroupAvatar,
    deleteGroup,
    exportCSV,
    importContacts
  };
};

export default useContactsService;
